// Phase JB.3c — World Cup card.
//
// Anon + auth visible. Lists the next 5 World Cup 2026 fixtures.
// Composes existing I0 primitives only (Eyebrow + TeamLine + Pill +
// Countdown + Badge). No primitive edits.
//
// Doctrine: rows with status='placeholder' carry a dedicated PLACEHOLDER
// Badge — N06 honesty: never present unverified data as confirmed.

import { type CSSProperties } from "react";
import { Badge, Countdown, Eyebrow, Pill, TeamLine } from "../super-acca";
import { useWorldCup, type WorldCupMatch } from "../../conversion/useWorldCup.ts";

const fontBody = "var(--sa-font-body)";
const fontMono = "var(--sa-font-mono)";

const wrapperStyle: CSSProperties = { padding: "8px 48px 8px" };
const cardStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 22,
  margin: "0 auto",
  maxWidth: 1180,
};

const STATUS_PILL_LABEL: Record<WorldCupMatch["status"], string> = {
  placeholder: "TBD",
  scheduled: "SCHEDULED",
  live: "LIVE",
  settled: "SETTLED",
  cancelled: "CANCELLED",
};

export interface WorldCupCardProps {
  /** Test override: pass matches directly. */
  matchesOverride?: WorldCupMatch[];
  /** Test override: force loading state. */
  loadingOverride?: boolean;
}

export function WorldCupCard({
  matchesOverride,
  loadingOverride,
}: WorldCupCardProps = {}): JSX.Element {
  const live = useWorldCup();
  const matches = matchesOverride ?? live.matches;
  const loading = loadingOverride ?? live.loading;

  return (
    <section
      aria-label="World Cup 2026 schedule"
      data-testid="world-cup-card"
      style={wrapperStyle}
    >
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Eyebrow>World Cup 2026 · Next 5</Eyebrow>
          <Badge variant="ghost">FIXTURES</Badge>
        </div>

        {loading ? (
          <p style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.18em", color: "var(--sa-fg-muted)", marginTop: 14 }}>
            LOADING…
          </p>
        ) : matches.length === 0 ? (
          <p
            data-testid="world-cup-empty"
            style={{ fontFamily: fontBody, fontSize: 13.5, color: "var(--sa-fg-secondary)", marginTop: 14, lineHeight: 1.7 }}
          >
            World Cup 2026 fixtures land here when the schedule confirms.
          </p>
        ) : (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
            {matches.map((m) => (
              <div
                key={m.match_id}
                data-testid={`world-cup-row-${m.match_id}`}
                data-status={m.status}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr auto auto auto",
                  gap: 14,
                  alignItems: "center",
                  borderBottom: "1px solid var(--sa-hairline)",
                  paddingBottom: 12,
                }}
              >
                <TeamLine
                  shortCode={m.home_team.slice(0, 3)}
                  name={m.home_team}
                  badgeUrl={null}
                  kitColor="var(--sa-accent-saffron)"
                />
                <TeamLine
                  shortCode={m.away_team.slice(0, 3)}
                  name={m.away_team}
                  badgeUrl={null}
                  kitColor="var(--sa-fg-muted)"
                />
                <span
                  style={{
                    fontFamily: fontMono,
                    fontSize: 10.5,
                    letterSpacing: "0.18em",
                    color: "var(--sa-fg-muted)",
                  }}
                >
                  {m.group_or_stage.replace(/_/g, " ")}
                </span>
                <Countdown targetISO={m.kickoff_at} compact />
                <div style={{ display: "flex", gap: 6 }}>
                  {m.status === "placeholder" ? (
                    <span data-testid={`world-cup-placeholder-${m.match_id}`}>
                      <Badge variant="ghost">PLACEHOLDER</Badge>
                    </span>
                  ) : (
                    <Pill>{STATUS_PILL_LABEL[m.status]}</Pill>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
